import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HappinessResultsSchema,
  MyHappinessSchema,
  type HappinessRatingValue,
  type MyHappinessDto,
} from '@taqon/contracts';
import { errorMessage, getData, putJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { StatCard, StatGrid } from '../../shared/ui/stats';
import { ActionFeedbackResults } from './ActionFeedbackResults';
import { FACES, FACE_LABEL, Mix, share, totalOf } from './faces';

const MINE = '/api/v1/happiness/mine';
const RESULTS = '/api/v1/happiness/results';
const COMMENT_LIMIT = 500;

/**
 * The Employee Happiness Meter (FRD-HRP-083 to HRP-089; CLR-44): a person gives their own rating
 * for today, and holders of the results permission see the counts and comments. Holders of the
 * feedback results permission also see how submitting a request went (FRD-CORE-066). Each half shows
 * only with its permission; the API decides either way.
 */
export function HappinessPage() {
  const session = useSession();
  const canRate = hasCapability(session.data, 'happiness.happiness_rating:create');
  const canView = hasCapability(session.data, 'happiness.happiness_rating:view');
  const canViewFeedback = hasCapability(session.data, 'happiness.action_feedback:view');
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Happiness meter"
        description="How people feel at work, one rating a day, and how submitting a request went. Ratings are anonymous: the results show counts and comments, never names."
      />
      {canRate && <MyRating />}
      {canView && <Results />}
      {canViewFeedback && <ActionFeedbackResults />}
      {!canRate && !canView && !canViewFeedback && (
        <Section>
          <EmptyState
            title="No access to the happiness meter"
            detail="Giving a rating and viewing the results each need a permission from an administrator."
          />
        </Section>
      )}
    </Stack>
  );
}

function MyRating() {
  const mine = useQuery({
    queryKey: ['happiness', 'mine'],
    queryFn: () => getData(MINE, MyHappinessSchema),
  });
  return (
    <Section title="How do you feel at work today?">
      {mine.isPending && <LoadingState label="Loading your rating" />}
      {mine.isError && <ErrorState message={errorMessage(mine.error)} />}
      {/* Keyed by the day, so a page left open overnight starts again from the new day. */}
      {mine.data !== undefined && <RatingForm key={mine.data.today} saved={mine.data} />}
    </Section>
  );
}

/**
 * Today's rating. Once it is saved the form gives way to what was saved, so the comment does not
 * sit in the box as if unsent; "Change my rating" reopens the form with it, because a rating given
 * again replaces the day's rating (FRD-HRP-085) and a cleared box would remove the comment.
 */
function RatingForm({ saved }: { saved: MyHappinessDto }) {
  const [editing, setEditing] = useState(saved.rating === null);
  const [justSaved, setJustSaved] = useState(false);
  if (editing || saved.rating === null) {
    return (
      <EditRating
        saved={saved}
        onSaved={() => {
          setEditing(false);
          setJustSaved(true);
        }}
        onCancel={
          saved.rating === null
            ? undefined
            : () => {
                setEditing(false);
              }
        }
      />
    );
  }
  const face = FACES.find((entry) => entry.value === saved.rating);
  return (
    <Stack spacing={2} sx={{ p: 2.5 }} aria-label="My rating for today" role="group">
      {justSaved && (
        <Alert severity="success" variant="outlined">
          Your rating for today is saved. You can change it until the end of today.
        </Alert>
      )}
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            color: `${face?.color ?? 'text'}.main`,
            '& svg': { fontSize: 40 },
          }}
        >
          {face?.icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Today you feel
          </Typography>
          <Typography sx={{ fontWeight: 600 }}>{FACE_LABEL[saved.rating]}</Typography>
        </Box>
      </Stack>
      <Typography
        variant="body2"
        color={saved.comment === null ? 'text.secondary' : 'text.primary'}
        sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
      >
        {saved.comment === null ? 'No comment.' : `Your comment: ${saved.comment}`}
      </Typography>
      <Box>
        <Button
          variant="outlined"
          onClick={() => {
            setJustSaved(false);
            setEditing(true);
          }}
        >
          Change my rating
        </Button>
      </Box>
    </Stack>
  );
}

function EditRating({
  saved,
  onSaved,
  onCancel,
}: {
  saved: MyHappinessDto;
  onSaved: () => void;
  onCancel: (() => void) | undefined;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [rating, setRating] = useState<HappinessRatingValue | null>(saved.rating);
  const [comment, setComment] = useState(saved.comment ?? '');

  const save = useMutation({
    mutationFn: () =>
      putJson(
        MINE,
        { rating, comment: comment.trim() === '' ? null : comment.trim() },
        MyHappinessSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async (stored) => {
      queryClient.setQueryData(['happiness', 'mine'], stored);
      await queryClient.invalidateQueries({ queryKey: ['happiness', 'results'] });
      onSaved();
    },
  });

  const unchanged = rating === saved.rating && comment.trim() === (saved.comment ?? '');
  return (
    <Stack
      spacing={2.5}
      sx={{ p: 2.5 }}
      component="form"
      aria-label="My rating for today"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (rating !== null) save.mutate();
      }}
    >
      {save.isError && (
        <Alert severity="error" variant="outlined">
          {errorMessage(save.error)}
        </Alert>
      )}
      <ToggleButtonGroup
        exclusive
        value={rating}
        aria-label="How you feel today"
        onChange={(_event, next: HappinessRatingValue | null) => {
          // A click on the chosen face keeps it: a rating cannot be withdrawn, only changed.
          if (next !== null) setRating(next);
        }}
        sx={{ flexWrap: 'wrap', gap: 1.5 }}
      >
        {FACES.map((face) => (
          <ToggleButton
            key={face.value}
            value={face.value}
            color={face.color}
            sx={{
              flex: '1 1 120px',
              flexDirection: 'column',
              gap: 0.5,
              py: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '12px !important',
              '& svg': { fontSize: 44 },
            }}
          >
            {face.icon}
            {face.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <TextField
        label="Comment"
        multiline
        minRows={2}
        value={comment}
        onChange={(event) => {
          setComment(event.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: COMMENT_LIMIT } }}
        helperText={`Optional. Shown in the results without your name. ${String(comment.length)} of ${String(COMMENT_LIMIT)} characters.`}
      />
      <Stack direction="row" spacing={1.5}>
        <Button
          type="submit"
          variant="contained"
          disabled={rating === null || unchanged || save.isPending}
        >
          {save.isPending
            ? 'Saving…'
            : saved.rating === null
              ? 'Save my rating'
              : 'Save the change'}
        </Button>
        {onCancel !== undefined && (
          <Button onClick={onCancel} disabled={save.isPending}>
            Cancel
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

function Results() {
  // The period the API was asked for; empty means its default, the last 30 days.
  const [period, setPeriod] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);

  const query = new URLSearchParams();
  if (period.from !== '') query.set('from', period.from);
  if (period.to !== '') query.set('to', period.to);
  const results = useQuery({
    queryKey: ['happiness', 'results', period.from, period.to],
    queryFn: () =>
      getData(
        query.size === 0 ? RESULTS : `${RESULTS}?${query.toString()}`,
        HappinessResultsSchema,
      ),
  });

  const shownFrom = from ?? results.data?.from ?? period.from;
  const shownTo = to ?? results.data?.to ?? period.to;
  const total = results.data === undefined ? 0 : totalOf(results.data.counts);
  return (
    <>
      <Section title="Results">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ p: 2.5, alignItems: { sm: 'flex-start' } }}
          component="form"
          aria-label="Results period"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            setPeriod({ from: shownFrom, to: shownTo });
          }}
        >
          <TextField
            type="date"
            label="From"
            value={shownFrom}
            onChange={(event) => {
              setFrom(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="date"
            label="To"
            value={shownTo}
            onChange={(event) => {
              setTo(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Button type="submit" variant="outlined" sx={{ mt: { sm: 1 } }}>
            Show
          </Button>
        </Stack>
        {results.isPending && <LoadingState label="Loading the results" />}
        {results.isError && <ErrorState message={errorMessage(results.error)} />}
      </Section>
      {results.data !== undefined && (
        <>
          <StatGrid cols={4}>
            <StatCard label="Ratings" value={total} icon={<HowToVoteIcon />} />
            {FACES.map((face) => (
              <StatCard
                key={face.value}
                label={face.label}
                value={`${share(results.data.counts[face.value], total)} (${String(results.data.counts[face.value])})`}
                icon={face.icon}
              />
            ))}
          </StatGrid>
          <Section title="By day">
            {results.data.days.length === 0 ? (
              <EmptyState
                title="No ratings in this period"
                detail="Choose another period, or wait for ratings to be given."
              />
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="Ratings by day">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Happy</TableCell>
                      <TableCell align="right">Neutral</TableCell>
                      <TableCell align="right">Unhappy</TableCell>
                      <TableCell align="right">Ratings</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>Mix</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.data.days.map((day) => (
                      <TableRow key={day.date}>
                        <TableCell>{day.date}</TableCell>
                        <TableCell align="right">{day.happy}</TableCell>
                        <TableCell align="right">{day.neutral}</TableCell>
                        <TableCell align="right">{day.unhappy}</TableCell>
                        <TableCell align="right">{totalOf(day)}</TableCell>
                        <TableCell>
                          <Mix counts={day} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Section>
          <Section title="Comments">
            {results.data.comments.length === 0 ? (
              <EmptyState title="No comments in this period" />
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="Comments">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Comment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.data.comments.map((comment, index) => (
                      // A comment carries no identifier, by design: its place in the list is its key.
                      <TableRow key={index}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{comment.ratedOn}</TableCell>
                        <TableCell>{FACE_LABEL[comment.rating]}</TableCell>
                        <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                          {comment.comment}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            {results.data.commentsTruncated && (
              <Typography variant="caption" sx={{ display: 'block', p: 2 }}>
                Only the most recent comments are shown. Choose a shorter period to see the rest.
              </Typography>
            )}
          </Section>
        </>
      )}
    </>
  );
}
